var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { h } from 'preact';
import { useState, useEffect, useMemo } from 'preact/hooks';
import { useSearchProvider } from '../search/useSearchProvider';
import { TrackingProvider } from '../analytics/context';
import { Layout } from '../components/layout/Layout';
import { FilterPanel } from '../components/filters/FilterPanel';
import { SortDropdown } from '../components/sorting/SortDropdown';
import { ProductGrid } from '../components/product/ProductGrid';
import { Pagination } from '../components/pagination/Pagination';
import { InfiniteScroll } from '../components/infinite-scroll/InfiniteScroll';
import { ToggleButton } from '../components/common/ToggleButton';
import { SearchResultsHeader } from '../components/search/SearchResultsHeader';
import { DidYouMean } from '../components/search/DidYouMean';
import { PageList } from '../components/content/PageList';
import { ArticleList } from '../components/content/ArticleList';
import { defaultDemoConfig } from '../config/demoConfig';
import { ConfigManager } from '../utils/configManager';
import { fetchBoostSearchSuggest } from '../platforms/boost/api';
import styles from './Pages.module.scss';
export function UnifiedDemoPage() {
    var _a, _b;
    // Load config with environment variable priority
    const [config, setConfig] = useState(() => {
        const urlConfig = ConfigManager.getConfigFromUrl();
        if (urlConfig) {
            // URL config takes highest precedence
            return Object.assign(Object.assign({}, defaultDemoConfig), urlConfig);
        }
        // Use the new method that respects env var priority
        return ConfigManager.loadConfigWithEnvPriority();
    });
    // Layout state managed at demo level
    const [layout, setLayout] = useState({
        cardLayout: config.layout.cardLayout,
        filterLayout: config.layout.filterLayout,
    });
    // Initialize page from URL query params
    const getPageFromUrl = () => {
        const params = new URLSearchParams(window.location.search);
        const pageParam = params.get('page');
        return pageParam ? parseInt(pageParam, 10) : 1;
    };
    // Update URL with current page
    const updateUrlPage = (page) => {
        const url = new URL(window.location.href);
        if (page === 1) {
            url.searchParams.delete('page');
        }
        else {
            url.searchParams.set('page', String(page));
        }
        window.history.pushState({}, '', url.toString());
    };
    // Infinite scroll state
    const [accumulatedProducts, setAccumulatedProducts] = useState([]);
    // Mirrors accumulatedProducts: banners are page-scoped in meta, so they must
    // accumulate too (positions offset per page — see the accumulation effects).
    const [accumulatedBanners, setAccumulatedBanners] = useState([]);
    const [currentPage, setCurrentPage] = useState(() => getPageFromUrl());
    const [loadingMore, setLoadingMore] = useState(false);
    const [loadMoreClicks, setLoadMoreClicks] = useState(0);
    const LOAD_MORE_THRESHOLD = 2; // After this many clicks, switch to infinite scroll
    // Search results state
    const [activeTab, setActiveTab] = useState('products');
    const [pages, setPages] = useState([]);
    const [articles, setArticles] = useState([]);
    const [didYouMean, setDidYouMean] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [loadingContent, setLoadingContent] = useState(false);
    const [pageCounts, setPageCounts] = useState({
        products: 0,
        articles: 0,
        pages: 0,
    });
    // Save config when it changes
    useEffect(() => {
        ConfigManager.saveConfig(config);
    }, [config]);
    const [openFilterDrawer, setOpenFilterDrawer] = useState(false);
    // Handle config change
    const handleConfigChange = (newConfig) => {
        setConfig(newConfig);
        // Re-run search if provider or mock platform changes
        if (newConfig.search.provider !== config.search.provider ||
            (newConfig.search.provider === 'mock' &&
                newConfig.search.mockPlatform !== config.search.mockPlatform)) {
            window.location.reload(); // Reload to reinitialize provider
        }
    };
    const { products, filters, meta, sortOptions, loading, query, currentSort, appliedFilters, handleQuerySubmit: originalHandleQuerySubmit, handleFilterChange: originalHandleFilterChange, handleClearAllFilters: originalHandleClearAllFilters, handleSortChange: originalHandleSortChange, runSearch, } = useSearchProvider(config.search.provider, config.search.mockPlatform, config.pagination.itemsPerPage);
    // Collection banners — sourced from the Boost search response (`meta.banners`),
    // populated by `insert_banners=true` on the `/filter` request and surfaced
    // through `mapMeta` in `src/platforms/boost/mapper.ts`. Boost provides the
    // five base fields (`imgSrc`, `link`, `name`, `openInNewTab`, `position`);
    // the overlay fields on `BannerData` are optional and stay unset.
    //
    // Previously this read from a `data-banners` attribute that the Shopify Liquid
    // template serialised onto `#search-app` from the Shopify `collection` object.
    // That path was retired because the Liquid serialisation always emitted every
    // banner configured on the collection, even when the current filter/sort
    // combination would have hidden products the banner sat next to — leaving an
    // empty grid slot ("empty space") at the bottom of the PLP. Boost's
    // server-side `insert_banners` resolves the banner set against the same
    // merchandising rules that produced the product list, so the positions stay
    // consistent with what's actually rendered. Old code kept here for reference:
    //
    // const [collectionBanners] = useState<BannerData[]>(() => {
    //   const el = document.querySelector('#search-app');
    //   const raw = el?.getAttribute('data-banners');
    //   if (!raw) return [];
    //   try {
    //     return JSON.parse(raw) as BannerData[];
    //   } catch {
    //     return [];
    //   }
    // });
    const collectionBanners = (_a = meta === null || meta === void 0 ? void 0 : meta.banners) !== null && _a !== void 0 ? _a : [];
    // Surface context for product-tile tracking. A query means we're on a search
    // results page; otherwise it's a collection/PLP. `correlationId` stays
    // undefined for Boost until `meta.rid` is plumbed through `api.ts` — the
    // provider falls back to a client-side rid in the meantime. Memoised so the
    // many ProductCards under it don't re-read context on unrelated re-renders.
    const trackingValue = useMemo(() => {
        var _a;
        return ({
            itemListId: query ? 'search-results' : 'plp',
            pageType: query ? 'search_page' : 'collection_page',
            searchTerm: query || undefined,
            correlationId: (_a = meta === null || meta === void 0 ? void 0 : meta.analytics) === null || _a === void 0 ? void 0 : _a.queryID,
        });
    }, [query, (_b = meta === null || meta === void 0 ? void 0 : meta.analytics) === null || _b === void 0 ? void 0 : _b.queryID]);
    // Wrap handlers to reset infinite scroll state
    const handleQuerySubmit = (newQuery) => {
        setAccumulatedProducts([]);
        setAccumulatedBanners([]);
        setCurrentPage(1);
        setLoadMoreClicks(0);
        updateUrlPage(1);
        setActiveTab('products'); // Reset to products tab on new search
        originalHandleQuerySubmit(newQuery);
    };
    const handleFilterChange = (filterId, value) => {
        setAccumulatedProducts([]);
        setAccumulatedBanners([]);
        setCurrentPage(1);
        setLoadMoreClicks(0);
        updateUrlPage(1);
        originalHandleFilterChange(filterId, value);
    };
    const handleClearAllFilters = () => {
        setAccumulatedProducts([]);
        setAccumulatedBanners([]);
        setCurrentPage(1);
        setLoadMoreClicks(0);
        updateUrlPage(1);
        originalHandleClearAllFilters();
    };
    const handleSortChange = (sortId) => {
        setAccumulatedProducts([]);
        setAccumulatedBanners([]);
        setCurrentPage(1);
        setLoadMoreClicks(0);
        updateUrlPage(1);
        originalHandleSortChange(sortId);
    };
    // Handle tab change
    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };
    // Handle "did you mean" suggestion click
    const handleDidYouMeanClick = (suggestion) => {
        handleQuerySubmit(suggestion);
    };
    // Fetch all search content when query changes
    useEffect(() => {
        if (query && config.search.provider === 'boost') {
            setLoadingContent(true);
            fetchBoostSearchSuggest(query, 1, config.pagination.itemsPerPage)
                .then((response) => {
                // Set pages
                const pageResults = (response.pages || []).map(page => ({
                    id: page.id,
                    title: page.title,
                    handle: page.handle,
                    url: page.url || `/pages/${page.handle}`,
                    body_html: page.body_html,
                    image: page.image ? { src: page.image } : undefined,
                }));
                setPages(pageResults);
                // Set articles (collections)
                const articleResults = (response.collections || []).map(collection => ({
                    id: collection.id,
                    title: collection.title,
                    handle: collection.handle,
                    url: `/collections/${collection.handle}`,
                    body_html: collection.body_html,
                    image: collection.image ? { src: collection.image } : undefined,
                }));
                setArticles(articleResults);
                // Set did you mean suggestions
                setDidYouMean(response.did_you_mean || []);
                // Set suggestions for autocomplete
                setSuggestions(response.suggestions || []);
                // Update counts (initial counts from suggest API)
                setPageCounts(prev => (Object.assign(Object.assign({}, prev), { articles: response.total_collection || articleResults.length, pages: response.total_page || pageResults.length })));
            })
                .catch((error) => {
                console.error('Error fetching search content:', error);
                setPages([]);
                setArticles([]);
                setDidYouMean([]);
            })
                .finally(() => {
                setLoadingContent(false);
            });
        }
    }, [query, config.search.provider, config.pagination.itemsPerPage]);
    // Update product count when meta changes (filters, sort, etc.)
    useEffect(() => {
        if (meta && query) {
            setPageCounts(prev => (Object.assign(Object.assign({}, prev), { products: meta.totalResults || 0 })));
        }
    }, [meta, query]);
    // Accumulate products for pagination infinite scroll
    useEffect(() => {
        var _a;
        if (config.pagination.type === 'pagination' && products.length > 0) {
            if ((meta === null || meta === void 0 ? void 0 : meta.currentPage) === 1) {
                // First page, replace all products
                setAccumulatedProducts(products);
                setAccumulatedBanners(collectionBanners);
            }
            else {
                // Subsequent pages, append products and offset this page's banners into
                // the combined grid (earlier pages are always full-size, matching the
                // assumption in loadLessProducts).
                const offset = (((_a = meta === null || meta === void 0 ? void 0 : meta.currentPage) !== null && _a !== void 0 ? _a : 1) - 1) * config.pagination.itemsPerPage;
                setAccumulatedProducts(prev => [...prev, ...products]);
                setAccumulatedBanners(prev => [
                    ...prev,
                    ...collectionBanners.map(b => (Object.assign(Object.assign({}, b), { position: b.position + offset }))),
                ]);
            }
            setLoadingMore(false);
        }
        else if (config.pagination.type !== 'pagination') {
            // For non-pagination types, just use products directly
            setAccumulatedProducts([]);
            setAccumulatedBanners([]);
        }
    }, [products, meta === null || meta === void 0 ? void 0 : meta.currentPage, config.pagination.type]);
    // Load more function for pagination infinite scroll
    const loadMoreProducts = () => {
        if (!runSearch || loadingMore || !meta)
            return;
        const nextPage = meta.currentPage + 1;
        if (nextPage > meta.totalPages)
            return;
        setLoadingMore(true);
        setCurrentPage(nextPage);
        updateUrlPage(nextPage);
        runSearch(query || '', {
            page: nextPage,
            pageSize: config.pagination.itemsPerPage,
            sort: currentSort || undefined,
            filters: appliedFilters,
        });
    };
    // Load less function - go back one page
    const loadLessProducts = () => {
        if (!meta || currentPage <= 1)
            return;
        const previousPage = currentPage - 1;
        // Remove products from the last page
        const itemsPerPage = config.pagination.itemsPerPage;
        const newProductCount = previousPage * itemsPerPage;
        setAccumulatedProducts(prev => prev.slice(0, newProductCount));
        setAccumulatedBanners(prev => prev.filter(b => b.position < newProductCount));
        setCurrentPage(previousPage);
        updateUrlPage(previousPage);
    };
    // Track initial mount to prevent duplicate searches
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);
    // Handle browser back/forward navigation
    useEffect(() => {
        const handlePopState = () => {
            const pageFromUrl = getPageFromUrl();
            if (pageFromUrl !== currentPage) {
                setCurrentPage(pageFromUrl);
                // Optionally reload products for this page
            }
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [currentPage]);
    // Load products up to the page specified in URL on initial mount
    useEffect(() => {
        if (!mounted || !runSearch || config.pagination.type !== 'pagination')
            return;
        const urlPage = getPageFromUrl();
        if (urlPage > 1 && meta && meta.currentPage < urlPage) {
            // Need to load all pages up to urlPage
            const loadPages = () => __awaiter(this, void 0, void 0, function* () {
                for (let page = 2; page <= urlPage; page++) {
                    if (page > meta.totalPages)
                        break;
                    yield new Promise((resolve) => {
                        setLoadingMore(true);
                        setCurrentPage(page);
                        runSearch(query || '', {
                            page: page,
                            pageSize: config.pagination.itemsPerPage,
                            sort: currentSort || undefined,
                            filters: appliedFilters,
                        });
                        // Wait a bit for the search to complete
                        setTimeout(resolve, 500);
                    });
                }
                setLoadingMore(false);
            });
            loadPages();
        }
    }, [mounted, meta === null || meta === void 0 ? void 0 : meta.totalPages, config.pagination.type]);
    // Update layout based on config
    useEffect(() => {
        setLayout({
            cardLayout: config.layout.cardLayout,
            filterLayout: config.layout.filterLayout,
        });
    }, [config.layout.cardLayout, config.layout.filterLayout]);
    // Reset to first page when pagination type changes
    useEffect(() => {
        if (!mounted)
            return;
        setAccumulatedProducts([]);
        setAccumulatedBanners([]);
        setCurrentPage(1);
        if (runSearch) {
            runSearch(query || '', {
                page: 1,
                pageSize: config.pagination.itemsPerPage,
                sort: currentSort || undefined,
                filters: appliedFilters,
            });
        }
    }, [config.pagination.type]);
    // Re-run search when items per page changes
    useEffect(() => {
        if (!mounted)
            return;
        setAccumulatedProducts([]);
        setAccumulatedBanners([]);
        setCurrentPage(1);
        runSearch(query, {
            pageSize: config.pagination.itemsPerPage,
            sort: currentSort || undefined,
            filters: appliedFilters,
        });
    }, [config.pagination.itemsPerPage]);
    // Accumulate products for infinite scroll
    useEffect(() => {
        const isInfiniteScroll = config.pagination.type === 'infiniteAuto' ||
            config.pagination.type === 'infiniteButton';
        if (isInfiniteScroll && products.length > 0) {
            if (currentPage === 1) {
                // First page, replace all products
                setAccumulatedProducts(products);
                setAccumulatedBanners(collectionBanners);
            }
            else {
                // Subsequent pages, append products and offset this page's banners into
                // the combined grid.
                const offset = (currentPage - 1) * config.pagination.itemsPerPage;
                setAccumulatedProducts((prev) => [...prev, ...products]);
                setAccumulatedBanners((prev) => [
                    ...prev,
                    ...collectionBanners.map(b => (Object.assign(Object.assign({}, b), { position: b.position + offset }))),
                ]);
            }
            setLoadingMore(false);
        }
    }, [products, currentPage, config.pagination.type]);
    // Helper to get product count display
    const getProductCountDisplay = () => {
        if (!meta)
            return '';
        const isInfiniteScroll = config.pagination.type === 'infiniteAuto' ||
            config.pagination.type === 'infiniteButton';
        if (isInfiniteScroll) {
            const displayedCount = accumulatedProducts.length;
            return `Showing 1 - ${displayedCount} of ${meta.totalResults} products`;
        }
        else {
            const start = meta.pageSize * (meta.currentPage - 1) + 1;
            const end = Math.min(meta.pageSize * meta.currentPage, meta.totalResults);
            return `Showing ${start} - ${end} of ${meta.totalResults} products`;
        }
    };
    // Render main content based on pagination type and active tab
    const renderMainContent = () => {
        // Show pages when pages tab is active
        if (query && activeTab === 'pages') {
            return h(PageList, { pages: pages, loading: loadingContent });
        }
        // Show articles when articles tab is active
        if (query && activeTab === 'articles') {
            return h(ArticleList, { articles: articles, loading: loadingContent });
        }
        // Show no results message when search shows no products
        if (query && !loading && pageCounts.products === 0) {
            return (h("div", { class: styles.noResults },
                h("p", { class: styles.noResultsText },
                    "No search results found for \"",
                    query,
                    "\"")));
        }
        // Force re-render by using key prop
        const contentKey = `${config.pagination.type}-${layout.cardLayout}-${layout.filterLayout}`;
        switch (config.pagination.type) {
            case 'none':
                return (h(ProductGrid, { key: contentKey, products: products, loading: loading, layout: layout, banners: collectionBanners }));
            case 'pagination': {
                const useInfiniteScroll = loadMoreClicks >= LOAD_MORE_THRESHOLD;
                const hasMore = meta ? currentPage < meta.totalPages : false;
                if (useInfiniteScroll) {
                    return (h("div", { key: contentKey },
                        h(InfiniteScroll, { products: accumulatedProducts, loading: loading || loadingMore, layout: layout, banners: accumulatedBanners, mode: "auto", hasMore: hasMore, onLoadMore: loadMoreProducts, showProductCount: true, totalResults: meta === null || meta === void 0 ? void 0 : meta.totalResults })));
                }
                return (h("div", { key: contentKey },
                    h(ProductGrid, { products: accumulatedProducts.length > 0 ? accumulatedProducts : products, loading: loading && currentPage === 1, layout: layout, banners: accumulatedProducts.length > 0 ? accumulatedBanners : collectionBanners }),
                    meta && (h(Pagination, { meta: meta, currentPage: currentPage, onChange: () => {
                            setLoadMoreClicks(prev => prev + 1);
                            loadMoreProducts();
                        }, onLoadLess: loadLessProducts }))));
            }
            case 'infiniteAuto':
                return (h(InfiniteScroll, { key: contentKey, products: accumulatedProducts, loading: loading || loadingMore, layout: layout, banners: accumulatedBanners, mode: "auto", pageSize: config.pagination.itemsPerPage, hasMore: meta ? currentPage < meta.totalPages : false, onLoadMore: loadMoreProducts }));
            case 'infiniteButton':
                return (h(InfiniteScroll, { key: contentKey, products: accumulatedProducts, loading: loading || loadingMore, layout: layout, banners: accumulatedBanners, mode: "button", pageSize: config.pagination.itemsPerPage, hasMore: meta ? currentPage < meta.totalPages : false, onLoadMore: loadMoreProducts }));
            default:
                return (h(ProductGrid, { key: contentKey, products: products, loading: loading, layout: layout, banners: collectionBanners }));
        }
    };
    // Simple layout without Layout component
    const renderSimpleLayout = () => (h("div", { class: "demo-page" },
        config.features.searchBar && (h("div", { class: "search-bar" },
            h("input", { type: "text", value: query, onChange: (e) => handleQuerySubmit(e.currentTarget.value), placeholder: "Search products..." }),
            h("button", { onClick: () => handleQuerySubmit(query) }, "Search"))),
        config.features.productCount && meta && (h("div", { class: styles.productCount }, getProductCountDisplay())),
        renderMainContent()));
    // Complex layout with Layout component
    const renderComplexLayout = () => (h("div", { class: "demo-page" },
        h(Layout, { key: `layout-${layout.filterLayout}-${layout.cardLayout}`, layout: layout, openFilterDrawer: openFilterDrawer, setOpenFilterDrawer: setOpenFilterDrawer, sidebar: config.features.filters ? (h(FilterPanel, { layout: layout.filterLayout, filters: filters, onFilterChange: handleFilterChange, onClearAll: handleClearAllFilters, totalResults: (meta === null || meta === void 0 ? void 0 : meta.totalResults) || 0, openFilterDrawer: openFilterDrawer, setOpenFilterDrawer: setOpenFilterDrawer })) : null, main: h("div", null,
                query && meta && (h(SearchResultsHeader, { query: query, totalResults: activeTab === 'products'
                        ? pageCounts.products
                        : activeTab === 'pages'
                            ? pageCounts.pages
                            : pageCounts.articles, activeTab: activeTab, onTabChange: handleTabChange, productCount: pageCounts.products, articleCount: pageCounts.articles, pageCount: pageCounts.pages, showTabs: !query || pageCounts.products > 0 || pageCounts.articles > 0 || pageCounts.pages > 0 })),
                didYouMean.length > 0 && (h(DidYouMean, { suggestions: didYouMean, onSuggestionClick: handleDidYouMeanClick })),
                (!query || pageCounts.products > 0) && (config.features.sorting ||
                    (config.features.filters &&
                        layout.filterLayout === 'filterDrawer') ||
                    config.features.productCount) && (h("div", { class: styles.productsHeader },
                    h("div", { class: styles.headerActions },
                        config.features.filters &&
                            (h(ToggleButton, { toggle: openFilterDrawer, setToggle: setOpenFilterDrawer, text: "Filters", selectedFilterCount: filters.flatMap(f => (f.options || []).filter(o => o.selected)).length })),
                        config.features.sorting && activeTab === 'products' && (h(SortDropdown, { options: sortOptions, currentSort: currentSort, onChange: handleSortChange }))))),
                renderMainContent()) })));
    // Determine which layout to use
    const shouldUseComplexLayout = config.features.filters || config.features.sorting;
    return (h(TrackingProvider, { value: trackingValue }, shouldUseComplexLayout ? renderComplexLayout() : renderSimpleLayout()));
}
